import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { useApiQuery } from '../useApiQuery';
import { apiDelete, apiGet, apiPost, operationsClient } from '@/lib/api';
import {
  normalizeAttachmentFile,
  validateAttachmentFile,
  type AttachmentFile,
} from '@/lib/attachments/attachment-files';
import { contactKeys, taskKeys } from '@/lib/queryKeys';

export type TaskAttachment = AttachmentFile;

function taskAttachmentsKey(contactId: string, taskId: string) {
  return [...contactKeys.detail(contactId), 'task-attachments', taskId] as const;
}

function invalidateTaskAttachmentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  contactId: string,
  taskId: string,
) {
  void queryClient.invalidateQueries({ queryKey: taskAttachmentsKey(contactId, taskId) });
  void queryClient.invalidateQueries({ queryKey: [...contactKeys.detail(contactId), 'tasks'] });
  void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
}

export function useTaskAttachments(contactId: string, taskId: string, enabled = true) {
  return useApiQuery<TaskAttachment[]>({
    queryKey: taskAttachmentsKey(contactId, taskId),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>[]>(
        operationsClient,
        `/contact-tasks/${taskId}/attachments`,
      );
      return raw.map(normalizeAttachmentFile);
    },
    enabled: enabled && Boolean(contactId && taskId),
    showErrorToast: false,
  });
}

export async function getTaskAttachmentDownloadUrl(taskId: string, attachmentId: string) {
  const raw = await apiGet<Record<string, unknown>>(
    operationsClient,
    `/contact-tasks/${taskId}/attachments/${attachmentId}/download-url`,
  );
  return String(raw.downloadUrl ?? raw.url ?? '');
}

export function validateTaskAttachment(file: File): string | null {
  return validateAttachmentFile(file);
}

export async function uploadTaskFile(taskId: string, file: File): Promise<TaskAttachment> {
  const error = validateTaskAttachment(file);
  if (error) throw new Error(error);

  const raw = await apiPost<Record<string, unknown>>(
    operationsClient,
    `/contact-tasks/${taskId}/attachments/upload-url`,
    { fileName: file.name, contentType: file.type, fileSize: file.size },
  );

  const uploadUrl = String(raw.uploadUrl ?? '');
  const attachment = normalizeAttachmentFile((raw.attachment ?? {}) as Record<string, unknown>);

  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });
  if (!response.ok) throw new Error(`Upload failed (${response.status})`);

  return attachment;
}

export function useUploadTaskFile(contactId: string, taskId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<TaskAttachment, File>({
    mutationFn: (file) => uploadTaskFile(taskId, file),
    onSuccess: () => {
      invalidateTaskAttachmentQueries(queryClient, contactId, taskId);
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to upload attachment');
    },
  });
}

export function useDeleteTaskFile(contactId: string, taskId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ deleted: boolean }, string>({
    mutationFn: (attachmentId) =>
      apiDelete<{ deleted: boolean }>(
        operationsClient,
        `/contact-tasks/${taskId}/attachments/${attachmentId}`,
      ),
    onSuccess: () => {
      invalidateTaskAttachmentQueries(queryClient, contactId, taskId);
      toast.success('Attachment deleted');
    },
  });
}
